import {useCallback} from 'react';

import {showSnackbar} from '@/app/shared-components/Snackbar/SnackbarProvider';
import {SnackbarType} from '@/app/shared-components/Snackbar/SnackbarTypes';
import logError, {
  FeatureNameTags,
  logBreadcrumb,
} from '@/utils/errors/logError';
import {maskPhoneNumber} from '@/utils/maskPhoneNumber';

import {
  useConfirmPaymentMutation,
  useJoinLoggedUserToSplitMutation,
  useLazyGetSplitDataQuery,
  useResendParticipantOtpMutation,
  useSaveParticipantPhoneMutation,
  useValidateParticipantOtpMutation,
} from './api';
import {
  IConfirmPaymentRequest,
  IGetSplitDataResponse,
  IJoinLoggedUserToSplitRequest,
  IResendOtpRequest,
  ISaveParticipantPhoneRequest,
  ISaveParticipantPhoneResponse,
  IValidateOtpRequest,
} from './entities';
import {useSplitsApiTrackers} from './trackers';

const fileName = 'splits - useSplitsRepository.ts';

const SPLITS_USER_ERRORS_API = {
  INVALID_OTP: {
    TITLE: 'Invalid OTP',
    MESSAGE: 'The OTP is invalid or has expired. Please request a new one.',
  },
  RESEND_OTP: {
    TITLE: 'Could not resend code',
    MESSAGE:
      'We could not send a new code. Please check your connection and try again.',
  },
  SAVE_PARTICIPANT_PHONE: {
    TITLE: 'Error saving participant phone',
    MESSAGE: 'There was an error saving your phone number. Please try again.',
  },
  CONFIRM_PAYMENT: {
    TITLE: 'Error confirming payment',
    MESSAGE: 'There was an error confirming your payment. Please try again.',
  },
  JOIN_LOGGED_USER_TO_SPLIT: {
    TITLE: 'Error joining logged user to split',
    MESSAGE:
      'There was an error joining the logged user to the split. Please try again.',
  },
} as const;

export const useSplitsRepository = (userId?: string) => {
  const [
    triggerGetSplitData,
    {isLoading: isLoadingSplitData, data: splitData, isError, error},
  ] = useLazyGetSplitDataQuery();

  const [triggerSaveParticipantPhone] = useSaveParticipantPhoneMutation();
  const [triggerValidateParticipantOtp] = useValidateParticipantOtpMutation();

  const [triggerConfirmPayment] = useConfirmPaymentMutation();
  const [triggerResendParticipantOtp] = useResendParticipantOtpMutation();

  const [triggerJoinLoggedUserToSplit] = useJoinLoggedUserToSplitMutation();

  const {
    trackApiSplitsSaveParticipantSuccess,
    trackApiSplitsSaveParticipantError,
    trackApiSplitsConfirmPaymentSuccess,
    trackApiSplitsConfirmPaymentError,
    trackApiSplitsValidateOtpSuccess,
    trackApiSplitsValidateOtpError,
    trackApiSplitsResendOtpSuccess,
    trackApiSplitsResendOtpError,
    trackApiSplitsJoinLoggedUserToSplitSuccess,
    trackApiSplitsJoinLoggedUserToSplitError,
  } = useSplitsApiTrackers(splitData?.owner_id, userId);

  const handleGetSplitData = useCallback(
    async (split_id: string): Promise<IGetSplitDataResponse> => {
      try {
        logBreadcrumb({
          category: `${FeatureNameTags.SPLITS}.useSplitsRepository.handleGetSplitData`,
          type: 'query',
          message: 'Get split data - API call',
          data: {
            split_id,
          },
        });
        const result = await triggerGetSplitData({split_id}).unwrap();
        return result;
      } catch (err) {
        logError(err, {
          customTitle: 'Splits - Error fetching split data',
          fileName: 'splits - useSplitsRepository.ts',
          functionName: 'handleGetSplitData',
          data: {split_id},
        });
        throw err;
      }
    },
    [triggerGetSplitData],
  );

  const handleSaveParticipantPhone = useCallback(
    async (
      params: ISaveParticipantPhoneRequest,
    ): Promise<ISaveParticipantPhoneResponse> => {
      const maskedPhone = maskPhoneNumber(params.phone_number);
      try {
        logBreadcrumb({
          category: `${FeatureNameTags.SPLITS}.useSplitsRepository.handleSaveParticipantPhone`,
          type: 'query',
          message: 'Save participant phone - API call',
          data: {
            split_id: params.split_id,
            phone_number: maskedPhone,
            name: params.name,
          },
        });
        const result = await triggerSaveParticipantPhone(params).unwrap();
        trackApiSplitsSaveParticipantSuccess(
          params.split_id,
          maskedPhone,
          result?.participant_id,
        );
        return result;
      } catch (error) {
        trackApiSplitsSaveParticipantError(
          params.split_id,
          maskedPhone,
          error instanceof Error ? error.message : String(error),
        );
        showSnackbar({
          variant: SnackbarType.ERROR,
          summary: SPLITS_USER_ERRORS_API.SAVE_PARTICIPANT_PHONE.TITLE,
          message: SPLITS_USER_ERRORS_API.SAVE_PARTICIPANT_PHONE.MESSAGE,
        });
        logError(error, {
          customTitle: 'Splits - Error saving participant phone',
          fileName,
          functionName: 'handleSaveParticipantPhone',
          data: {
            split_id: params.split_id,
            phone_number: maskedPhone,
            name: params.name,
          },
        });
        throw error;
      }
    },
    [
      trackApiSplitsSaveParticipantError,
      trackApiSplitsSaveParticipantSuccess,
      triggerSaveParticipantPhone,
    ],
  );

  const handleConfirmPayment = useCallback(
    async (params: IConfirmPaymentRequest): Promise<void> => {
      try {
        logBreadcrumb({
          category: `${FeatureNameTags.SPLITS}.useSplitsRepository.handleConfirmPayment`,
          type: 'query',
          message: 'Mark split participant as paid - API call',
          data: {
            params,
          },
        });
        const result = await triggerConfirmPayment(params).unwrap();
        trackApiSplitsConfirmPaymentSuccess(
          params.split_id,
          params.participant_id,
        );
        return result;
      } catch (error) {
        trackApiSplitsConfirmPaymentError(
          params.split_id,
          params.participant_id,
          error instanceof Error ? error.message : String(error),
        );
        showSnackbar({
          variant: SnackbarType.ERROR,
          summary: SPLITS_USER_ERRORS_API.CONFIRM_PAYMENT.TITLE,
          message: SPLITS_USER_ERRORS_API.CONFIRM_PAYMENT.MESSAGE,
        });
        logError(error, {
          customTitle: 'Splits - Error confirming payment',
          fileName,
          functionName: 'handleConfirmPayment',
          data: {params},
        });
        throw error;
      }
    },
    [
      trackApiSplitsConfirmPaymentError,
      trackApiSplitsConfirmPaymentSuccess,
      triggerConfirmPayment,
    ],
  );

  const handleValidateOtp = useCallback(
    async (params: IValidateOtpRequest): Promise<void> => {
      try {
        logBreadcrumb({
          category: `${FeatureNameTags.SPLITS}.useSplitsRepository.handleValidateOtp`,
          type: 'query',
          message: 'Validate participant OTP - API call',
          data: {
            split_id: params.split_id,
            participant_id: params.participant_id,
          },
        });
        const result = await triggerValidateParticipantOtp(params).unwrap();
        trackApiSplitsValidateOtpSuccess(
          params.split_id,
          params.participant_id,
        );
        return result;
      } catch (error) {
        showSnackbar({
          variant: SnackbarType.ERROR,
          summary: SPLITS_USER_ERRORS_API.INVALID_OTP.TITLE,
          message: SPLITS_USER_ERRORS_API.INVALID_OTP.MESSAGE,
        });
        logError(error, {
          customTitle: 'Splits - Error validating OTP',
          fileName: 'splits - useSplitsRepository.ts',
          functionName: 'handleValidateOtp',
          data: {
            split_id: params.split_id,
            participant_id: params.participant_id,
          },
        });
        trackApiSplitsValidateOtpError(
          params.split_id,
          params.participant_id,
          error instanceof Error ? error.message : String(error),
        );
        throw error;
      }
    },
    [
      trackApiSplitsValidateOtpError,
      trackApiSplitsValidateOtpSuccess,
      triggerValidateParticipantOtp,
    ],
  );

  const handleResendOtp = useCallback(
    async (params: IResendOtpRequest): Promise<void> => {
      try {
        logBreadcrumb({
          category: `${FeatureNameTags.SPLITS}.useSplitsRepository.handleResendOtp`,
          type: 'query',
          message: 'Resend participant OTP - API call',
          data: {
            split_id: params.split_id,
            participant_id: params.participant_id,
          },
        });
        const result = await triggerResendParticipantOtp(params).unwrap();
        trackApiSplitsResendOtpSuccess(params.split_id, params.participant_id);
        return result;
      } catch (error) {
        trackApiSplitsResendOtpError(
          params.split_id,
          params.participant_id,
          error instanceof Error ? error.message : String(error),
        );
        showSnackbar({
          variant: SnackbarType.ERROR,
          summary: SPLITS_USER_ERRORS_API.RESEND_OTP.TITLE,
          message: SPLITS_USER_ERRORS_API.RESEND_OTP.MESSAGE,
        });
        logError(error, {
          customTitle: 'Splits - Error resending OTP',
          fileName,
          functionName: 'handleResendOtp',
          data: {
            split_id: params.split_id,
            participant_id: params.participant_id,
          },
        });
        throw error;
      }
    },
    [
      trackApiSplitsResendOtpError,
      trackApiSplitsResendOtpSuccess,
      triggerResendParticipantOtp,
    ],
  );

  const handleJoinLoggedUserToSplit = useCallback(
    async (params: IJoinLoggedUserToSplitRequest): Promise<boolean> => {
      try {
        logBreadcrumb({
          category: `${FeatureNameTags.SPLITS}.useSplitsRepository.handleJoinLoggedUserToSplit`,
          type: 'query',
          message: 'Join logged user to split - API call',
          data: {
            split_id: params.split_id,
            participant_id: params.participant_id,
          },
        });
        await triggerJoinLoggedUserToSplit(params).unwrap();
        trackApiSplitsJoinLoggedUserToSplitSuccess(
          params.split_id,
          params.participant_id,
        );
        return true;
      } catch (error) {
        trackApiSplitsJoinLoggedUserToSplitError(
          params.split_id,
          params.participant_id,
          error instanceof Error ? error.message : String(error),
        );
        showSnackbar({
          variant: SnackbarType.ERROR,
          summary: SPLITS_USER_ERRORS_API.JOIN_LOGGED_USER_TO_SPLIT.TITLE,
          message: SPLITS_USER_ERRORS_API.JOIN_LOGGED_USER_TO_SPLIT.MESSAGE,
        });
        logError(error, {
          customTitle: 'Splits - Error joining logged user to split',
          fileName,
          functionName: 'handleJoinLoggedUserToSplit',
          data: {
            split_id: params.split_id,
            participant_id: params.participant_id,
          },
        });
        return false;
      }
    },
    [
      trackApiSplitsJoinLoggedUserToSplitError,
      trackApiSplitsJoinLoggedUserToSplitSuccess,
      triggerJoinLoggedUserToSplit,
    ],
  );

  return {
    handleGetSplitData,
    handleSaveParticipantPhone,
    handleValidateOtp,
    handleConfirmPayment,
    handleResendOtp,
    handleJoinLoggedUserToSplit,
    splitData,
    isLoadingSplitData,
    isError,
    error,
  };
};
